// Ô địa chỉ của kho (lấy hàng / hoàn hàng) và sổ địa chỉ khách quen: AddressInput (địa chỉ cũ / mới,
// một ô Khu vực) + nút chọn trên bản đồ. Giữ nguyên props để WarehouseForm, CustomerAddressDialog không đổi.

import { useState } from 'react'
import { MapPin } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { fetchProvincesApi, matchAdminDivisions } from '@/lib/provincesApi'
import { resolveAdministrativeAddress } from '@/lib/vietnamAddressResolver'
import { AddressMapPickerModal, type MapPickedAddress } from './AddressMapPickerModal'
import { AddressInput } from './address/AddressInput'

export interface SmartAddressValue {
  senderAddress: string
  senderCity: string
  senderDistrict?: string | null
  senderWard: string
  lat?: number | null
  lng?: number | null
}

interface SmartAddressFieldsProps {
  value: SmartAddressValue
  onChange: (patch: Partial<SmartAddressValue>) => void
  disabled?: boolean
}

export function SmartAddressFields({ value, onChange, disabled }: SmartAddressFieldsProps) {
  const [mapModalOpen, setMapModalOpen] = useState(false)

  // Chỉ để khớp tên tỉnh / quận / phường lấy từ bản đồ (như trước)
  const { data: provinceList = [] } = useQuery({
    queryKey: ['provinces-api'],
    queryFn: fetchProvincesApi,
    staleTime: 1000 * 60 * 60 * 24,
  })

  const handleMapConfirm = async (picked: MapPickedAddress) => {
    const resolved = resolveAdministrativeAddress({
      city: picked.city,
      district: picked.district,
      ward: picked.ward,
      street: picked.address,
      fullAddress: picked.formattedAddress,
    })
    // Biết phường CŨ của điểm ghim → lưu địa chỉ cũ (BEST giao / lấy hàng đúng phường). Không thì địa chỉ mới
    // (2 cấp) — AddressInput tìm / hỏi phường cũ
    const { city, district, ward } = picked.old ?? await matchAdminDivisions(provinceList, {
      city: resolved.city || picked.city,
      district: resolved.district || picked.district,
      ward: resolved.ward || picked.ward,
      fullText: picked.formattedAddress,
    })
    const address = resolved.street || picked.address
    onChange({
      senderAddress: address || value.senderAddress,
      senderCity: city || value.senderCity,
      senderDistrict: city ? district : value.senderDistrict,
      senderWard: ward || (city ? '' : value.senderWard),
      lat: picked.lat,
      lng: picked.lng,
    })
    toast.success(`Đã định vị: ${[address, ward, district, city].filter(Boolean).join(', ')}`)
  }

  return (
    <div className="space-y-2">
      <AddressInput
        value={{
          street: value.senderAddress ?? '',
          city: value.senderCity ?? '',
          district: value.senderDistrict ?? null,
          ward: value.senderWard ?? '',
        }}
        onChange={(v) => onChange({ senderAddress: v.street, senderCity: v.city, senderDistrict: v.district, senderWard: v.ward })}
        disabled={disabled}
        resolveNew
        coords={value.lat != null && value.lng != null ? { lat: value.lat, lng: value.lng } : null}
      />
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={() => setMapModalOpen(true)} className="gap-1.5">
          <MapPin className="size-4 text-accent" /> Chọn trên bản đồ
        </Button>
        {value.lat != null && value.lng != null && (
          <span className="font-mono text-accent">
            ({value.lat.toFixed(4)}, {value.lng.toFixed(4)})
          </span>
        )}
      </div>

      <AddressMapPickerModal
        open={mapModalOpen}
        onClose={() => setMapModalOpen(false)}
        onConfirm={handleMapConfirm}
        initialLat={value.lat}
        initialLng={value.lng}
        initialAddressText={[value.senderAddress, value.senderWard, value.senderDistrict, value.senderCity].filter(Boolean).join(', ')}
      />
    </div>
  )
}
